// ==========================================================
// FILE: src/app/core/localdata/sync/sync-core.service.ts
// ==========================================================
import { Injectable } from '@angular/core';
import { ProductLocalService } from '../core-sqlite-services/productos/product-local.service';
import { ProductPriceLocalService } from '../core-sqlite-services/productos/product-price-local.service';
import { SrvProductService } from 'src/app/modules/productos/service-product/srv-product.service';
import { SrvProductPriceService } from 'src/app/modules/productos/service-product-price/srv-product-price.service';

interface Syncable<T> {
  getAll(): Promise<T[]>;
  update(item: T): Promise<void>;
  createOrUpdate(item: T): Promise<any>;
  softDelete?(item: T): Promise<any>;
}

@Injectable({ providedIn: 'root' })
export class SyncCoreService {
  private syncHandlers: { name: string; service: Syncable<any> }[] = [];

  constructor(
    private productLocal: ProductLocalService,
    private productPriceLocal: ProductPriceLocalService,
    private productApi: SrvProductService,
    private productPriceApi: SrvProductPriceService
  ) {
    // ==========================================================
    // PRODUCTOS: crear o actualizar en backend
    // ==========================================================
    const createOrUpdateProductOnline = async (item: any) => {
      item.price_base_type = item.price_base_type === 'TTC' ? 'TTC' : 'HT';

      if (item.synced === 1 && item.backend_id) {
        const payload = { ...item, rowid: item.backend_id };
        return this.productApi.updateProduct(payload as any).toPromise();
      }

      const payload = { ...item };
      delete payload.rowid;
      delete payload.backend_id;

      const onlineProduct = await this.productApi.createProduct(payload as any).toPromise();
      console.log('[SyncCoreService] Producto creado online:', onlineProduct);

      if (item.rowid && onlineProduct?.rowid) {
        await this.productLocal.updateBackendId(item.rowid, onlineProduct.rowid);

        // 🔹 Reasignar precios locales inmediatamente al nuevo ID backend
        const localPrices = await this.productPriceLocal.getPricesByProduct(item.rowid);
        for (const p of localPrices) {
          // Actualiza fk_product y marca como no sincronizado
          const updatedPrice = { ...p, fk_product: onlineProduct.rowid, synced: 0 };
          await this.productPriceLocal.updatePrice(updatedPrice);

          // 🔹 Sincronizar el precio inmediatamente
          try {
            await createOrUpdatePriceOnline(updatedPrice);
          } catch (err) {
            console.warn('[SyncCoreService] Error sincronizando precio inmediatamente:', err);
          }
        }
      }

      return onlineProduct;
    };

    // ==========================================================
    // PRECIOS: crear o actualizar en backend
    // ==========================================================
    const createOrUpdatePriceOnline = async (item: any) => {
      item.price_base_type = item.price_base_type === 'TTC' ? 'TTC' : 'HT';

      // Resolver fk_product usando rowid local si aún no tiene backend_id
      if ((!item.fk_product || item.fk_product === 0) && item.fk_product_local) {
        try {
          const localProduct = await this.productLocal.getProductByRowId(item.fk_product_local);
          if (localProduct?.backend_id) {
            item.fk_product = localProduct.backend_id;
          }
        } catch (err) {
          console.warn('[SyncCoreService] No se pudo resolver fk_product_local:', err);
        }
      }

      if (!item.fk_product || item.fk_product === 0) {
        console.warn('[SyncCoreService] Precio sin fk_product válido, se omite:', item);
        return Promise.resolve();
      }

      const localRowid = item.rowid;
      const payload = { ...item };

      if (!item.backend_id) {
        delete payload.rowid;
        delete payload.backend_id;
      } else {
        payload.rowid = item.backend_id;
      }

      const result = item.backend_id
        ? await this.productPriceApi.updatePrice(payload as any).toPromise()
        : await this.productPriceApi.createPrice(payload as any).toPromise();

      // Actualizar backend_id local y marcar como sincronizado
      try {
        const returnedRowid = result?.rowid ?? null;
        if (localRowid && returnedRowid) {
          await this.productPriceLocal.updateBackendId(localRowid, returnedRowid);
        } else if (item.rowid && item.backend_id) {
          item.synced = 1;
          await this.productPriceLocal.updatePrice(item);
        }
      } catch (err) {
        console.warn('[SyncCoreService] Error al actualizar backend_id local:', err);
      }

      return result;
    };

    // ==========================================================
    // HANDLERS: Productos primero, luego Precios
    // ==========================================================
    this.syncHandlers = [
      {
        name: 'Productos',
        service: {
          getAll: () => this.productLocal.getAllProducts(),
          update: item => this.productLocal.updateProduct(item),
          createOrUpdate: createOrUpdateProductOnline,
          softDelete: item =>
            item.rowid
              ? this.productApi.deleteProduct(item.backend_id ?? item.rowid).toPromise()
              : Promise.resolve()
        }
      },
      {
        name: 'Precios',
        service: {
          getAll: () => this.productPriceLocal.getAllPrices(),
          update: item => this.productPriceLocal.updatePrice(item),
          createOrUpdate: createOrUpdatePriceOnline,
          softDelete: item =>
            item.rowid
              ? this.productPriceApi.deletePrice(item.backend_id ?? item.rowid).toPromise()
              : Promise.resolve()
        }
      }
    ];
  }

  // ==========================================================
  // MÉTODO PRINCIPAL DE SINCRONIZACIÓN
  // ==========================================================
  async syncAll(): Promise<void> {
    console.log('🔹 Iniciando sincronización completa...');
    for (const handler of this.syncHandlers) {
      console.log(`🔹 Sincronizando ${handler.name}...`);
      const items = await handler.service.getAll();
      const unsynced = items.filter(i => i.synced === 0 || i.status === 2);

      for (const item of unsynced) {
        try {
          if (item.status === 2 && handler.service.softDelete) {
            await handler.service.softDelete(item);
            console.log(`🗑️ ${handler.name} eliminado: ${item.ref || item.rowid}`);
          } else {
            const result = await handler.service.createOrUpdate(item);
            console.log(`✅ ${handler.name} sincronizado: ${item.ref || item.rowid}`, result);
          }

          // Marcar como sincronizado solo si no es precio pendiente de producto
          if (handler.name !== 'Precios' || item.fk_product) {
            item.synced = 1;
          }
          await handler.service.update(item);
        } catch (err) {
          console.error(`❌ Error sincronizando ${handler.name}:`, err);
        }
      }
    }
    console.log('🔹 Sincronización completa.');
  }
}
